import {API_BASE} from '../apiConfig';

/**
 * Связь на старте приложения: первые запросы к серверу (проверка
 * авторизации) с тайм-аутом и диагностика, почему они не прошли.
 *
 * Без тайм-аута «висящая» сеть — включенный VPN без связи, блокировка адреса,
 * Wi-Fi без выхода в интернет — держала fetch минутами, и игрок смотрел на
 * бесконечный сплеш без единого слова о том, что случилось. Мгновенный отказ
 * (режим полета) ловился и раньше, а вот тишина — нет.
 *
 * Диагностика отличает три случая, советы игроку в них разные:
 * - нет интернета вообще — проверить Wi-Fi/мобильную сеть, VPN;
 * - интернет есть, а до нашего сервера не достучаться — сменить сеть,
 *   выключить VPN или, наоборот, включить, сменить его локацию;
 * - сервер отвечает, но ошибкой — у нас сбой или обновление, ждать.
 */

export const CONNECTIVITY_STATUS = {
  OFFLINE: 'offline',
  SERVER_UNREACHABLE: 'server_unreachable',
  SERVER_ERROR: 'server_error',
  SERVER_OK: 'server_ok',
};

// Проверка авторизации — маленький JSON: если за это время ответа нет, дело
// не в медленной сети, а в том, что запрос не доходит
export const AUTH_CHECK_TIMEOUT_MS = 15000;
// Пробы диагностики идут параллельно, это потолок всей проверки
export const CONNECTIVITY_PROBE_TIMEOUT_MS = 6000;

// Сервер пробуем той же ручкой, которую ждет старт: /time отвечает и при
// упавшей базе, и экран «нет связи» тогда метался бы между сплешем и самим
// собой — проба «сервер жив», повторная проверка авторизации снова падает
const SERVER_PROBE_PATH = '/auth/me';

// «Интернет вообще есть?» — крупные сервисы разных юрисдикций: при
// ограничениях мобильного интернета открываются только отечественные, через
// сломанный VPN — ни один. Достаточно ответа любого. Тело не читаем (no-cors
// дает непрозрачный ответ): важен сам факт, что запрос сходил и вернулся
export const INTERNET_PROBE_URLS = [
  'https://www.gstatic.com/generate_204',
  'https://ya.ru/favicon.ico',
  'https://vk.com/favicon.ico',
];

const SERVER_PROBE_RESULT = {
  OK: 'ok',
  ERROR: 'error',
  UNREACHABLE: 'unreachable',
};

export class RequestTimeoutError extends Error {
  constructor(url, timeoutMs) {
    super(`Request to ${url} timed out after ${timeoutMs} ms`);
    this.name = 'RequestTimeoutError';
  }
}

const defaultFetch = (...args) => fetch(...args);

/**
 * fetch, который через timeoutMs отклоняется с RequestTimeoutError и
 * обрывает сам запрос (если браузер умеет AbortController)
 */
export const fetchWithTimeout = (url, options = {}, timeoutMs, fetchImpl = defaultFetch) => {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let timeoutId = null;
  const timeout = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      // Сначала отклоняем свое обещание, потом обрываем запрос: обрыв
      // отклоняет fetch с AbortError, и в гонке он не должен успеть первым
      reject(new RequestTimeoutError(url, timeoutMs));
      controller?.abort();
    }, timeoutMs);
  });
  const request = fetchImpl(url, controller ? {...options, signal: controller.signal} : options);
  return Promise.race([request, timeout]).finally(() => clearTimeout(timeoutId));
};

/**
 * Ошибка означает, что запрос не дошел до сервера или ответ не вернулся:
 * fetch отклоняется TypeError'ом только при сетевом сбое
 */
export const checkIfNetworkFailure = (error) => (
  error instanceof TypeError
  || error instanceof RequestTimeoutError
  || error?.name === 'AbortError'
);

const readIsBrowserOnline = () => (
  typeof navigator === 'undefined' || navigator.onLine !== false
);

export const probeGameServer = async ({fetchImpl, timeoutMs = CONNECTIVITY_PROBE_TIMEOUT_MS} = {}) => {
  try {
    const response = await fetchWithTimeout(
      `${API_BASE}${SERVER_PROBE_PATH}`,
      {method: 'GET', credentials: 'include', cache: 'no-store'},
      timeoutMs,
      fetchImpl,
    );
    // 401 гостя — тоже ответ живого сервера. 5xx — сервер (или nginx перед
    // ним) ответил, что сделать ничего не может
    return response.status >= 500 ? SERVER_PROBE_RESULT.ERROR : SERVER_PROBE_RESULT.OK;
  } catch (error) {
    return SERVER_PROBE_RESULT.UNREACHABLE;
  }
};

export const probeInternet = ({
  fetchImpl,
  timeoutMs = CONNECTIVITY_PROBE_TIMEOUT_MS,
  urls = INTERNET_PROBE_URLS,
} = {}) => new Promise((resolve) => {
  let pendingCount = urls.length;
  if (!pendingCount) {
    resolve(false);
    return;
  }
  const handleFailure = () => {
    pendingCount -= 1;
    if (pendingCount === 0) {
      resolve(false);
    }
  };
  urls.forEach((url) => {
    fetchWithTimeout(
      url,
      // Без кук и реферера: сторонним сервисам незачем знать, откуда проба
      {mode: 'no-cors', cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer'},
      timeoutMs,
      fetchImpl,
    ).then(() => resolve(true), handleFailure);
  });
});

/**
 * Почему сервер недоступен — см. CONNECTIVITY_STATUS. SERVER_OK значит, что
 * связь уже восстановилась и проверку авторизации пора повторить
 */
export const diagnoseConnectivity = async ({
  fetchImpl,
  timeoutMs = CONNECTIVITY_PROBE_TIMEOUT_MS,
  isBrowserOnline = readIsBrowserOnline(),
  internetProbeUrls = INTERNET_PROBE_URLS,
} = {}) => {
  // navigator.onLine === false надежен (сети нет совсем), а вот true ничего
  // не гарантирует — тогда решают пробы
  if (!isBrowserOnline) {
    return CONNECTIVITY_STATUS.OFFLINE;
  }
  const [serverProbe, hasInternet] = await Promise.all([
    probeGameServer({fetchImpl, timeoutMs}),
    probeInternet({fetchImpl, timeoutMs, urls: internetProbeUrls}),
  ]);
  if (serverProbe === SERVER_PROBE_RESULT.OK) {
    return CONNECTIVITY_STATUS.SERVER_OK;
  }
  if (serverProbe === SERVER_PROBE_RESULT.ERROR) {
    return CONNECTIVITY_STATUS.SERVER_ERROR;
  }
  return hasInternet ? CONNECTIVITY_STATUS.SERVER_UNREACHABLE : CONNECTIVITY_STATUS.OFFLINE;
};
